import { useEffect, useMemo, useState } from "react";
import { Scatter } from "react-chartjs-2";
import type { ChartData, ChartOptions, ScatterDataPoint } from "chart.js";
import { getProfileChart } from "../services/api";
import { convertLegacyData, normalizeLegacyDatasets } from "../converters/chartData";
import type { LegacyLineDataset, ProfileChartRootArgs } from "../types";
import ChartFullscreenWrapper from "./ChartFullscreenWrapper";
import Loader from "./Loader";

type Props = ProfileChartRootArgs;

export default function ProfileChart(props: Props) {
  const [loading, setLoading] = useState(true);
  const [datasets, setDatasets] = useState<LegacyLineDataset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!props.isNominalProfile) {
      return;
    }

    const refresh = () => setRefreshToken((value) => value + 1);
    const jquery = (window as Window & {
      jQuery?: (target: Document) => {
        on: (events: string, handler: () => void) => void;
        off: (events: string, handler: () => void) => void;
      };
    }).jQuery?.(document);
    const events = "siisel.nominalPointsUpdated siisel.curvePointUpdated";

    jquery?.on(events, refresh);
    document.addEventListener("siisel.nominalPointsUpdated", refresh);
    document.addEventListener("siisel.curvePointUpdated", refresh);

    return () => {
      jquery?.off(events, refresh);
      document.removeEventListener("siisel.nominalPointsUpdated", refresh);
      document.removeEventListener("siisel.curvePointUpdated", refresh);
    };
  }, [props.isNominalProfile]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    void getProfileChart(props.profileId, props.nominalProfileId, props.isNominalProfile ?? false)
      .then((result) => {
        if (!mounted) {
          return;
        }
        setDatasets(normalizeLegacyDatasets(result.dataSets ?? result.datasets ?? result.DataSets));
      })
      .catch(() => {
        if (mounted) {
          setDatasets([]);
          setError("Nepodařilo se načíst graf.");
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [props.isNominalProfile, props.nominalProfileId, props.profileId, refreshToken]);

  const chartData = useMemo<ChartData<"scatter", ScatterDataPoint[]>>(() => {
    return convertLegacyData(datasets);
  }, [datasets]);

  const chartOptions = useMemo<ChartOptions<"scatter">>(() => {
    const unionData = datasets.flatMap((dataset) => dataset.data ?? []);
    const values = unionData.flatMap((point) => [Number(point.x), Number(point.y)]).filter((x) => Number.isFinite(x));

    const max = values.length > 0 ? Math.max(...values) : 0;
    const min = values.length > 0 ? Math.min(...values) : 0;

    const stepSize = 1;
    const units = Math.ceil(Math.max(max, Math.abs(min)) / stepSize);
    const axisMax = units * stepSize;
    const axisMin = Math.sign(min || -1) * axisMax;

    return {
      maintainAspectRatio: false,
      aspectRatio: 1,
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: false,
          text: "Profile Chart"
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const point = (context.dataset.data[context.dataIndex] as { metadata?: Record<string, unknown> }) ?? {};
              return (point.metadata?.tooltipText as string | undefined) ?? "Profile";
            }
          }
        }
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 5,
          bottom: 0
        }
      },
      scales: {
        "x-axis-1": {
          type: "linear",
          min: axisMin,
          max: axisMax,
          grid: {
            color: "#888",
            display: false,
            drawBorder: false,
            drawTicks: false
          },
          ticks: {
            display: false,
            stepSize
          }
        },
        "y-axis-1": {
          type: "linear",
          min: axisMin,
          max: axisMax,
          grid: {
            color: "#888",
            display: false,
            drawBorder: false,
            drawTicks: false
          },
          ticks: {
            display: false,
            stepSize
          }
        }
      }
    };
  }, [datasets]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div className="alert alert-warning">{error}</div>;
  }

  if (!datasets.some((dataset) => (dataset.data?.length ?? 0) > 0)) {
    return <div className="alert alert-info">Náhled není dostupný, nominální profil neobsahuje žádné body.</div>;
  }

  const size = props.height ?? 520;

  return (
    <div>
      <ChartFullscreenWrapper
        style={{
          ...(props.style ?? {}),
          width: "100%",
          maxWidth: size,
          height: size,
          margin: "0 auto",
          padding: "15px"
        }}
      >
        <Scatter data={chartData} options={chartOptions} />
      </ChartFullscreenWrapper>
    </div>
  );
}
